// src/layouts/applications/task-list/index.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { exportCsv } from "utils/exportCsv";
import { syncTasks } from "utils/sync";
import { getTasks, syncInvoice, deleteTask as deleteTaskBackend } from "../../../api/tasks";
import {
  assignTaskDriver,
  approveTaskOverride,
  denyTaskOverride,
  downloadProtectedFile,
  getBaseUrl,
  getTaskHistory,
  getTaskProofs,
  listDrivers,
  request,
  updateTaskStatus,
} from "../../../api/client";
import { v4 as uuidv4 } from "uuid";
import { getCurrentUser } from "../../../data/users";
import DashboardLayout from "../../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../examples/Navbars/DashboardNavbar";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
// Notifications
import MDSnackbar from "../../../components/MDSnackbar";
import { isReassignmentRequired, reassignmentMessage, getPendingOverride } from "../../../utils/dispatchOps";
import { getTaskCustomer, getTaskDropoffFacility, getDropoffLabel } from "../../../utils/taskDisplay";

const TASKS_KEY = "ci_tasks";
const EDIT_KEY = "ci_task_edit";
const API = getBaseUrl();

// 1.0.2 — simplified active workflow (no more "En Route" intermediate step,
// and "Paused" is no longer generated as a new active state). The dropdown
// below only offers these 7 values going forward. Historical/legacy tasks
// that already carry "En Route"/"En Route (PU)"/"En Route (DL)"/"Paused"
// are NOT rewritten — see normalizeActiveStatusForDisplay() just below,
// which maps them to their operationally-equivalent canonical value for the
// live dropdown's displayed value/color only (the stored task.status is
// untouched unless the dispatcher explicitly picks a new option).
const TASK_STATUSES = [
  "Assigned", "Accepted", "Arrived (PU)", "Picked Up", "Arrived", "Canceled", "Delivered",
];

/**
 * Maps a legacy/pre-1.0.2 active status to its canonical equivalent for
 * DISPLAY ONLY (the dropdown's `value` + color). Never mutates task data —
 * if the dispatcher changes the dropdown, updateTaskStatusBackend() sends
 * whatever NEW option they picked from the canonical list above.
 */
function normalizeActiveStatusForDisplay(rawStatus, task) {
  const key = String(rawStatus || "").trim().toLowerCase();
  if (key === "en route" || key === "en route (pu)" || key === "enroute") {
    return "Accepted";
  }
  if (key === "en route (dl)") {
    return "Picked Up";
  }
  if (key === "paused") {
    // Mirrors the driver app's own pause-resume logic (useTaskAction/
    // taskActions.ts): pickupProofCaptured tells us whether the pause
    // happened before or after pickup.
    return task && task.pickupProofCaptured ? "Picked Up" : "Accepted";
  }
  return rawStatus;
}

// Solid-fill backgrounds — white text over every chip for maximum contrast.
const TASK_STATUS_COLORS = {
  "assigned":        "#fb8c00",  // Orange
  "accepted":        "#03a9f4",  // Light Blue
  "en route":        "#9c27b0",  // Purple
  "enroute":         "#9c27b0",
  "arrived":         "#00897b",  // Teal — on location
  "picked up":       "#3f51b5",  // Indigo
  "picked_up":       "#3f51b5",
  "delivered":       "#4caf50",  // Green
  "paused":          "#757575",  // Dark Grey
  "canceled":        "#f44336",  // Red
  "emergency dispatch": "#f44336",
};

/* ---------------- helpers ---------------- */
const money = (v) =>
  typeof v === "number" && !Number.isNaN(v)
    ? v.toLocaleString(undefined, { style: "currency", currency: "USD" })
    : v && !Number.isNaN(Number(v))
    ? Number(v).toLocaleString(undefined, { style: "currency", currency: "USD" })
    : "—";

const dt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const trunc = (s, n = 42) =>
  !s ? "—" : s.length > n ? s.slice(0, n - 1) + "…" : s;

const isEmergencyTask = (t) =>
  t.isEmergency === true ||
  String(t.status || "").toLowerCase().includes("emergency") ||
  String(t.notes || "").startsWith("STAT / TRAUMA");

// Work-source derivation — the only safe signal we have at the task level.
// linkedCaseId present  → task is connected to an AxisSync network case.
// linkedCaseId absent   → task is a standalone native Call It In courier job.
// This is purely additive: neither branch changes task execution behavior.
const workSource = (t) => (t.linkedCaseId ? "axissync" : "native");

// AxisSync operational context helpers — only used for linked tasks.
// These parse fields that are reliably present on auto-generated case tasks.

// Extract surgeon name from the base_info block written into task notes by
// POST /cases/.  Format: "Case #: ... | Surgeon: Smith, John | Trays: ..."
// Returns null for native tasks, manually-created tasks, or when "N/A".
const parseSurgeonFromNotes = (notesStr) => {
  if (!notesStr) return null;
  const m = String(notesStr).match(/Surgeon:\s*([^|]+)/);
  const name = m ? m[1].trim() : null;
  return name && name !== "N/A" ? name : null;
};

// Parse the [PROC] prefix written by the Create Case form.
// Format: "[PROC] Left Hip – Primary | optional notes"
// Returns null for pre-Procedure tasks and native tasks.
const parseProcedureSummary = (notesStr) => {
  if (!notesStr) return null;
  const m = String(notesStr).match(/^\[PROC\]\s*([^\n|]+)/);
  return m ? m[1].trim() : null;
};

const tooltip = (t) => {
  const c = t?.charges || {};
  const bits = [
    c.base != null ? `Base ${money(c.base)}` : null,
    c.mileageCharge ? `Mileage ${money(c.mileageCharge)}` : null,
    c.deadheadCharge ? `Deadhead ${money(c.deadheadCharge)}` : null,
    c.weightCharge ? `Weight ${money(c.weightCharge)}` : null,
    c.waitCharge ? `Wait ${money(c.waitCharge)}` : null,
    c.holdoverCharge ? `Holdover ${money(c.holdoverCharge)}` : null,
    c.afterHoursCharge ? `After Hours ${money(c.afterHoursCharge)}` : null,
    c.dedicatedCharge ? `Dedicated ${money(c.dedicatedCharge)}` : null,
    c.fuelSurcharge ? `Fuel ${money(c.fuelSurcharge)}` : null,
    `Total ${money(t?.total ?? t?.price)}`,
  ].filter(Boolean);
  return bits.join(" • ");
};

const getTaskKey = (t) => String(t?.id || t?.number || "");

/* ---------------- risk / urgency ---------------- */
// Returns the deadline ISO string from whichever field is set.
const getDeadline = (t) =>
  t?.deliveryDeadline ||
  t?.milestones?.deliveryDeadline ||
  t?.deliverBy ||
  null;

// Risk levels based on time remaining until deadline.
function computeRisk(task) {
  const raw = getDeadline(task);
  if (!raw) return { level: null, minsLeft: null, label: "—", color: "#9CA3AF" };

  const minsLeft = (new Date(raw) - Date.now()) / 60000;

  if (minsLeft < 0)   return { level: "Critical", minsLeft, label: "OVERDUE",                       color: "#DC2626" };
  if (minsLeft < 30)  return { level: "Critical", minsLeft, label: `${Math.round(minsLeft)}m left`, color: "#DC2626" };
  if (minsLeft < 120) return { level: "High",     minsLeft, label: `${Math.round(minsLeft)}m left`, color: "#EA580C" };
  if (minsLeft < 240) {
    const h = Math.floor(minsLeft / 60), m = Math.round(minsLeft % 60);
    return { level: "Medium", minsLeft, label: `${h}h ${m}m left`, color: "#CA8A04" };
  }
  const h = Math.floor(minsLeft / 60), m = Math.round(minsLeft % 60);
  return { level: "Low", minsLeft, label: `${h}h ${m}m left`, color: "#16A34A" };
}

// Row background based on risk (emergency always wins).
function riskRowStyle(task, i, emerg) {
  if (emerg) return { background: "rgba(239,68,68,0.06)", outline: "1px solid rgba(239,68,68,0.25)" };
  if (isReassignmentRequired(task)) {
    return { background: "rgba(220,38,38,0.08)", borderLeft: "4px solid #DC2626", outline: "1px solid rgba(220,38,38,0.18)" };
  }
  const { level } = computeRisk(task);
  const rawDriver = String(task.assignedDriver || task.driver || "");
  const hasDriver = rawDriver && rawDriver.toLowerCase() !== "unassigned";
  if (level === "Critical") return { background: "rgba(220,38,38,0.06)", borderLeft: "3px solid #DC2626" };
  if (level === "High")     return { background: "rgba(234,88,12,0.05)", borderLeft: "3px solid #EA580C" };
  if (level === "Medium" && !hasDriver)
                            return { ...zebra(i),                         borderLeft: "2px solid #CA8A04" };
  return zebra(i);
}

// Whether a task has been stuck in a non-terminal, non-progressed status too long.
const TERMINAL_STATUSES = new Set(["delivered", "completed", "canceled"]);
const STALL_THRESHOLD_MS = 3 * 60 * 60 * 1000; // 3 hours

function isStalled(task) {
  const s = (task.status || "").toLowerCase();
  if (TERMINAL_STATUSES.has(s)) return false;
  if (!["assigned", "accepted"].includes(s)) return false;
  if (!task.createdAt) return false;
  return Date.now() - new Date(task.createdAt).getTime() > STALL_THRESHOLD_MS;
}

// getOverrideMeta and getPendingOverride are imported from dispatchOps.js
// (canonical versions that cover all three override types: pickup / arrival / delivery)

/* ---------------- layout styles ---------------- */
const page = { margin: "28px 0", padding: "0 24px" };
const headerBar = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 14,
};
const title = { fontSize: 22, fontWeight: 800, margin: 0 };
const actionsRow = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };

const searchBox = {
  width: 280,
  height: 40,
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  padding: "0 12px",
  fontSize: 14,
};

const card = {
  background: "#fff",
  borderRadius: 16,
  boxShadow: "0 8px 30px rgba(2,6,23,0.06)",
  padding: 16,
};

// Column order: # | Seq | Client | Pickup | Dropoff | Status | Driver | ETA | Total | Pieces | Weight | Created | Actions
// Column order: # | Seq | Client | Pickup | Dropoff | Deadline | Total | Pieces | Weight | Created | Status* | Driver* | Actions*
// (* = sticky right — Status, Driver, and Actions are always visible regardless of horizontal scroll)
const gridCols =
  "90px 58px 170px 260px 260px 140px 120px 80px 95px 175px 145px 200px 240px";

// ✅ Table should scroll horizontally instead of collapsing columns
const tableMinWidth = 2200;

// Sticky column left offsets — row has padding: "12px 12px" and grid has gap: 12px.
// Each value is the cell's distance from the scroll-container left edge.
const STICKY_ID_L  = 12;                        // col 1 (#)         : padding-left only
const STICKY_SEQ_L = 12 + 90 + 12;              // col 2 (Seq)       : 114
const STICKY_CLI_L = 12 + 90 + 12 + 58 + 12;   // col 3 (Client)    : 184
const STICKY_ACT_R = 12;                        // col 13 (Actions) : matches padding-right
const STICKY_DRV_R = 12 + 240 + 12;            // col 12 (Driver)  : 264 from right
const STICKY_STS_R = 12 + 240 + 12 + 200 + 12; // col 11 (Status)  : 476 from right — leftmost of the right-pinned group
const STICKY_HDR_Z = 3;
const STICKY_ROW_Z = 2;
const STICKY_HDR_BG = "#ffffff";

// Opaque row background for sticky cells — must be fully opaque so scrolling
// content behind the sticky columns doesn't bleed through.
function getStickyBg(task, i, emerg) {
  if (emerg || isReassignmentRequired(task)) return "#FEF2F2";
  const { level } = computeRisk(task);
  if (level === "Critical") return "#FEF2F2";
  if (level === "High")     return "#FFF7ED";
  return i % 2 ? "#fafbfc" : "#ffffff";
}

// Floating scroll arrow — appears at the left/right edge of the table container.
const scrollArrowBtn = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 20,
  background: "rgba(255,255,255,0.92)",
  border: "1px solid #D1D5DB",
  borderRadius: "50%",
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(0,0,0,0.14)",
  fontSize: 13,
  color: "#374151",
  padding: 0,
  lineHeight: 1,
};

const headRow = {
  display: "grid",
  gridTemplateColumns: gridCols,
  gap: 12,
  padding: "12px 12px",
  fontSize: 12,
  fontWeight: 800,
  color: "#6b7280",
  whiteSpace: "nowrap",
};

const row = {
  display: "grid",
  gridTemplateColumns: gridCols,
  gap: 12,
  alignItems: "start",   // top-align so multi-line cells don't displace single-line neighbours
  padding: "12px 12px",
  borderRadius: 12,
};

const zebra = (i) => (i % 2 ? { background: "#fafbfc" } : { background: "#ffffff" });

const cell = { fontSize: 12, color: "#374151" };
const mono = { fontVariantNumeric: "tabular-nums" };

// ✅ Prevent wrap + give ellipsis (fixes “last order broke alignment”)
const cellEllipsis = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const thBtn = {
  background: "transparent",
  border: 0,
  padding: 0,
  fontSize: 12,
  fontWeight: 800,
  color: "#6b7280",
  cursor: "pointer",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const btn = {
  border: "1px solid #E5E7EB",
  background: "#fff",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 12,
  cursor: "pointer",
};

const btnDanger = { ...btn, color: "#EF4444", borderColor: "#FECACA" };
const btnQB = {
  ...btn,
  color: "#059669",
  borderColor: "#A7F3D0",
  fontWeight: "bold",
  background: "#ECFDF5",
};

/* ---------------- sorting ---------------- */
const SORTERS = {
  id: (a, b) => getTaskKey(a).localeCompare(getTaskKey(b)),
  clientName: (a, b) => getTaskCustomer(a).localeCompare(getTaskCustomer(b)),
  driver: (a, b) => String(a?.driver || "").localeCompare(String(b?.driver || "")),
  total: (a, b) => Number(a?.total ?? a?.price ?? 0) - Number(b?.total ?? b?.price ?? 0),
  createdAt: (a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0),
  status: (a, b) => String(a?.status || "").localeCompare(String(b?.status || "")),
  // Phase 4 additions
  deadline: (a, b) => new Date(getDeadline(a) || "2099-01-01") - new Date(getDeadline(b) || "2099-01-01"),
  urgency:  (a, b) => (computeRisk(a).minsLeft ?? Infinity) - (computeRisk(b).minsLeft ?? Infinity),
};

// ─── TaskDetailDrawer ─────────────────────────────────────────────────────────
/**
 * Right-side inspection drawer for dispatch.
 * Read-only surface — zero mutations, no execution behavior changes.
 * All data comes from the task object already in the list state.
 */

const PROOF_LABEL = {
  none:                "No Proof",
  photo:               "📷 Photo",
  signature:           "✍️ Signature",
  photo_and_signature: "📷 + ✍️ Both",
};

/**
 * Convert a raw base64 string into a data URI for <img> rendering.
 * Detects JPEG vs PNG from the first few base64 characters so we don't
 * need a separate fileType field in the database.
 * Returns null when b64 is falsy.
 */
function dataUri(b64) {
  if (!b64) return null;
  // canvas.toDataURL() returns a full data URI (data:image/...;base64,...).
  // The driver app sends this directly to the backend which stores it as-is.
  // Older DB records therefore contain the full URI — pass through unchanged.
  if (b64.startsWith("data:")) return b64;
  // Newer records (post-backend-fix) store raw base64 only.
  // Detect JPEG vs PNG from the first few encoded bytes.
  const mime =
    b64.startsWith("/9j")    ? "image/jpeg"
    : b64.startsWith("iVBO") ? "image/png"
    : "image/jpeg";
  return `data:${mime};base64,${b64}`;
}

const STATUS_COLORS_DETAIL = {
  "assigned":       { bg: "#FFF7ED", color: "#C2410C", border: "#FED7AA" },
  "accepted":       { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
  "en route":       { bg: "#FAF5FF", color: "#6D28D9", border: "#DDD6FE" },
  "arrived (pu)":   { bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" },
  "picked up":      { bg: "#EEF2FF", color: "#4338CA", border: "#C7D2FE" },
  "en route (dl)":  { bg: "#FAF5FF", color: "#7C3AED", border: "#DDD6FE" },
  "arrived":        { bg: "#F0FDFA", color: "#0F766E", border: "#99F6E4" },
  "delivered":      { bg: "#F0FDF4", color: "#15803D", border: "#BBF7D0" },
  "paused":         { bg: "#F9FAFB", color: "#6B7280", border: "#E5E7EB" },
  "canceled":       { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA" },
};

// ─── Task history display helpers ─────────────────────────────────────────────
const STATUS_HISTORY_STYLE = {
  "assigned":      { color: "#C2410C", dot: "#FED7AA" },
  "accepted":      { color: "#1D4ED8", dot: "#93C5FD" },
  "en route":      { color: "#6D28D9", dot: "#C4B5FD" },
  "arrived (pu)":  { color: "#B45309", dot: "#FDE68A" },
  "picked up":     { color: "#4338CA", dot: "#A5B4FC" },
  "en route (dl)": { color: "#7C3AED", dot: "#C4B5FD" },
  "arrived":       { color: "#0F766E", dot: "#5EEAD4" },
  "delivered":     { color: "#15803D", dot: "#86EFAC" },
  "paused":        { color: "#6B7280", dot: "#D1D5DB" },
  "canceled":      { color: "#DC2626", dot: "#FCA5A5" },
};

const STATUS_HISTORY_LABEL = {
  "assigned":      "Assigned",
  "accepted":      "Accepted",
  "en route":      "En Route (PU)",
  "arrived (pu)":  "Arrived at Pickup",
  "picked up":     "Picked Up",
  "en route (dl)": "En Route (DL)",
  "arrived":       "Arrived at Dropoff",
  "delivered":     "Delivered",
  "paused":        "Paused",
  "canceled":      "Canceled",
};

function getHistoryEventDisplay(evt) {
  if (evt.eventType === "STATUS_CHANGED") {
    const key   = String(evt.newStatus || "").toLowerCase();
    const style = STATUS_HISTORY_STYLE[key] || { color: "#6B7280", dot: "#D1D5DB" };
    const label = STATUS_HISTORY_LABEL[key] || evt.newStatus || "Status Updated";
    return { label, dotColor: style.dot, textColor: style.color };
  }
  if (evt.eventType === "TASK_CREATED") {
    const ttype = evt.taskType ? ` (${evt.taskType})` : "";
    return { label: `Task Created${ttype}`, dotColor: "#D1D5DB", textColor: "#6B7280" };
  }
  if (evt.eventType === "TASK_GENERATED") {
    const ttype = evt.taskType ? ` (${evt.taskType})` : "";
    return { label: `Task Auto-Generated${ttype}`, dotColor: "#D1D5DB", textColor: "#6B7280" };
  }
  if (evt.eventType === "DRIVER_ASSIGNED") {
    const dname = evt.driverName ? ` — ${evt.driverName}` : "";
    return { label: `Driver Assigned${dname}`, dotColor: "#93C5FD", textColor: "#1D4ED8" };
  }
  if (evt.eventType === "OVERRIDE_REQUESTED") {
    const otype = evt.overrideType ? ` (${evt.overrideType})` : "";
    const note  = evt.reason ? ` — "${evt.reason}"` : "";
    return { label: `Override Requested${otype}${note}`, dotColor: "#FDE68A", textColor: "#B45309" };
  }
  if (evt.eventType === "OVERRIDE_APPROVED") {
    const otype = evt.overrideType ? ` (${evt.overrideType})` : "";
    const who   = evt.approvedBy ? ` by ${evt.approvedBy}` : "";
    return { label: `Override Approved${otype}${who}`, dotColor: "#86EFAC", textColor: "#15803D" };
  }
  if (evt.eventType === "OVERRIDE_DENIED") {
    const otype = evt.overrideType ? ` (${evt.overrideType})` : "";
    const who   = evt.deniedBy ? ` by ${evt.deniedBy}` : "";
    return { label: `Override Denied${otype}${who}`, dotColor: "#FCA5A5", textColor: "#DC2626" };
  }
  if (evt.eventType === "OVERRIDE_USED") {
    const otype = evt.overrideType ? ` (${evt.overrideType})` : "";
    return { label: `Override Used${otype} — Step Bypassed`, dotColor: "#FDA4AF", textColor: "#9F1239" };
  }
  if (evt.eventType === "PROOF_CAPTURED") {
    const isPickup  = evt.stage === "pickup";
    const hasPhoto  = evt.photoReceived;
    const hasSig    = evt.signatureReceived;
    const what      = hasPhoto && hasSig ? "Photo + Sig" : hasPhoto ? "Photo" : hasSig ? "Sig" : "Proof";
    const label     = isPickup
      ? `Pickup Proof Captured (${what})`
      : `Delivery Proof Captured (${what})`;
    return {
      label,
      dotColor:  isPickup ? "#FDE68A" : "#86EFAC",
      textColor: isPickup ? "#B45309" : "#15803D",
    };
  }
  return { label: evt.eventType, dotColor: "#D1D5DB", textColor: "#6B7280" };
}

/**
 * Read-only vertical timeline of task execution events for the inspection drawer.
 *
 * Props
 * ─────
 * events  – EventRecord[] from GET /tasks/{id}/history
 * hasAuditTrail – bool from the same response
 * loading – true while fetch is in flight
 */
function TaskHistorySection({ events, hasAuditTrail, loading }) {
  if (loading) {
    return (
      <DrawerSection label="Task History">
        <Typography sx={{ fontSize: 11, color: "#94A3B8" }}>Loading history…</Typography>
      </DrawerSection>
    );
  }

  if (!events?.length) {
    return (
      <DrawerSection label="Task History">
        <Typography sx={{ fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>
          No recorded events — this task pre-dates event tracking.
        </Typography>
      </DrawerSection>
    );
  }

  return (
    <DrawerSection label="Task History">
      {!hasAuditTrail && (
        <Typography sx={{ fontSize: 10, color: "#94A3B8", fontStyle: "italic", mb: 1 }}>
          Partial history — timestamps derived from task record (pre-audit task).
        </Typography>
      )}
      <Box sx={{ position: "relative", pl: 0 }}>
        {/* Vertical connector line */}
        <Box
          sx={{
            position: "absolute",
            left: 7,
            top: 10,
            bottom: 10,
            width: "1.5px",
            background: "#E5E7EB",
            zIndex: 0,
          }}
        />

        {events.map((evt, i) => {
          const { label, dotColor, textColor } = getHistoryEventDisplay(evt);
          const isLast = i === events.length - 1;
          const actorStr = (evt.actor || "").replace(/^system$/i, "").trim();

          return (
            <Box
              key={evt.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                position: "relative",
                mb: isLast ? 0 : 1.75,
              }}
            >
              {/* Timeline dot */}
              <Box
                sx={{
                  width: 15,
                  height: 15,
                  borderRadius: "50%",
                  background: dotColor,
                  border: "2px solid #fff",
                  boxShadow: `0 0 0 1.5px ${dotColor}`,
                  flexShrink: 0,
                  mt: "3px",
                  mr: 1.5,
                  zIndex: 1,
                }}
              />

              {/* Event content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: textColor,
                    lineHeight: 1.35,
                  }}
                >
                  {label}
                </Typography>
                {actorStr && (
                  <Typography sx={{ fontSize: 10, color: "#6B7280", lineHeight: 1.4 }}>
                    {actorStr}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 10, color: "#9CA3AF", mt: 0.2 }}>
                  {evt.timestamp
                    ? new Date(evt.timestamp).toLocaleString([], {
                        month:  "short",
                        day:    "numeric",
                        hour:   "2-digit",
                        minute: "2-digit",
                      })
                    : "—"}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </DrawerSection>
  );
}

function DrawerSection({ label, children }) {
  return (
    <Box mb={2.5}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "#94A3B8",
          mb: 1,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function DrawerRow({ label, value, mono = false, multiline = false }) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <Box display="flex" alignItems={multiline ? "flex-start" : "center"} gap={1} mb={0.75}>
      <Typography
        sx={{
          fontSize: 11,
          color: "#6B7280",
          minWidth: 130,
          flexShrink: 0,
          lineHeight: 1.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: "#111827",
          lineHeight: 1.5,
          fontVariantNumeric: mono ? "tabular-nums" : undefined,
          wordBreak: multiline ? "break-word" : undefined,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Renders a proof section (pickup or delivery) inside the task detail drawer.
 *
 * Props
 * ─────
 * label         – section heading e.g. "Proof of Pickup"
 * requirement   – raw requirement string from task ("none" | "photo" | "signature" | ...)
 * captured      – boolean derived from task blob flags (backward compat)
 * capturedAt    – ISO string or null
 * recipientName – string or null
 * proofRecord   – proof record from GET /tasks/{id}/pod, or null (legacy / not yet captured)
 *                 { photoData: base64|null, signatureData: base64|null, savedAt, driverEmail }
 * loading       – true while the GET /pod request is in flight
 */
function ProofSection({ label, taskId, publicId, stage, requirement, captured, capturedAt, recipientName, proofRecord, loading }) {
  const reqKey   = (requirement || "none").toLowerCase().replace(/-/g, "_");
  const reqLabel = PROOF_LABEL[reqKey] ?? reqKey;
  const noProof  = reqKey === "none";

  const photoUri = dataUri(proofRecord?.photoData ?? null);
  const sigUri   = dataUri(proofRecord?.signatureData ?? null);
  const hasImage = !!(photoUri || sigUri);

  // Per-image error state — set to true by onError so we never render a broken-image box.
  const [photoErr, setPhotoErr] = React.useState(false);
  const [sigErr,   setSigErr]   = React.useState(false);
  // Reset error flags whenever the proof record changes (different task opened).
  React.useEffect(() => { setPhotoErr(false); setSigErr(false); setDownloadErr(null); }, [proofRecord?.id]);

  // Photo download goes through the authenticated backend endpoint so the
  // file is the canonical stored image (and access rules are re-checked
  // server-side), rather than only re-saving what is already in the page.
  const [photoDownloading, setPhotoDownloading] = React.useState(false);
  const [downloadErr, setDownloadErr] = React.useState(null);
  const handlePhotoDownload = async () => {
    setPhotoDownloading(true);
    setDownloadErr(null);
    try {
      const ext = photoUri && photoUri.startsWith("data:image/png") ? "png" : "jpg";
      await downloadProtectedFile(
        `/tasks/${encodeURIComponent(taskId)}/pod/photo?stage=${encodeURIComponent(stage)}`,
        `pod-${stage}-${String(taskId).slice(0, 8)}.${ext}`
      );
    } catch (e) {
      setDownloadErr(e?.message || "Download failed.");
    } finally {
      setPhotoDownloading(false);
    }
  };

  // The delivery stage downloads the COMPLETE customer-facing POD (a PDF with the
  // delivery details, recipient, signature, photos and history) built by the
  // backend, instead of only the proof photo. The file name uses the customer-
  // facing order number only - never the internal task id.
  const isDeliveryStage = stage === "delivery";
  const [podDownloading, setPodDownloading] = React.useState(false);
  const handlePodDownload = async () => {
    setPodDownloading(true);
    setDownloadErr(null);
    try {
      let tz = "UTC";
      try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      } catch {
        // keep UTC
      }
      const orderRef = String(publicId || "").replace(/[^A-Za-z0-9-]/g, "");
      await downloadProtectedFile(
        `/tasks/${encodeURIComponent(publicId || taskId)}/pod/document?tz=${encodeURIComponent(tz)}`,
        orderRef ? `POD-${orderRef}.pdf` : "POD.pdf"
      );
    } catch (e) {
      setDownloadErr(e?.message || "Download failed.");
    } finally {
      setPodDownloading(false);
    }
  };

  return (
    <DrawerSection label={label}>
      {/* Requirement badge */}
      <Box display="flex" alignItems="center" gap={1} mb={0.75}>
        <Typography sx={{ fontSize: 11, color: "#6B7280", minWidth: 130, flexShrink: 0 }}>
          Requirement
        </Typography>
        <Chip
          label={reqLabel}
          size="small"
          sx={{
            fontSize: 10, fontWeight: 700,
            background: noProof ? "#F9FAFB" : "#FEF3C7",
            color:      noProof ? "#6B7280"  : "#B45309",
            border:     `1px solid ${noProof ? "#E5E7EB" : "#FDE68A"}`,
          }}
        />
      </Box>

      {/* Captured status */}
      {!noProof && (
        <Box display="flex" alignItems="center" gap={1} mb={0.75}>
          <Typography sx={{ fontSize: 11, color: "#6B7280", minWidth: 130, flexShrink: 0 }}>
            Status
          </Typography>
          {loading ? (
            <Typography sx={{ fontSize: 10, color: "#94A3B8" }}>Loading…</Typography>
          ) : captured ? (
            <Box display="flex" alignItems="center" gap={0.75}>
              <Chip
                label="✓ Captured"
                size="small"
                sx={{
                  fontSize: 10, fontWeight: 700,
                  background: "#F0FDF4", color: "#15803D",
                  border: "1px solid #BBF7D0",
                }}
              />
              {(capturedAt || proofRecord?.savedAt) && (
                <Typography sx={{ fontSize: 10, color: "#6B7280" }}>
                  {new Date(capturedAt || proofRecord?.savedAt).toLocaleString([], {
                    month: "short", day: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </Typography>
              )}
            </Box>
          ) : (
            <Chip
              label="Not Yet Captured"
              size="small"
              sx={{
                fontSize: 10, fontWeight: 700,
                background: "#FFF7ED", color: "#C2410C",
                border: "1px solid #FED7AA",
              }}
            />
          )}
        </Box>
      )}

      {/* Recipient name */}
      {recipientName && <DrawerRow label="Recipient" value={recipientName} />}

      {/* Driver email (if available from proof record) */}
      {proofRecord?.driverEmail && (
        <DrawerRow label="Captured By" value={proofRecord.driverEmail} />
      )}

      {/* Complete POD download (delivery stage only) */}
      {isDeliveryStage && !loading && captured && (
        <Box mt={1.25} mb={0.5}>
          <Box
            component="button"
            type="button"
            disabled={podDownloading}
            onClick={handlePodDownload}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              border: "1px solid #4338CA",
              borderRadius: "8px",
              background: podDownloading ? "#EEF2FF" : "#4338CA",
              color: podDownloading ? "#4338CA" : "#FFFFFF",
              fontSize: 12,
              fontWeight: 700,
              cursor: podDownloading ? "wait" : "pointer",
              "&:hover": { background: podDownloading ? "#EEF2FF" : "#3730A3" },
            }}
          >
            {podDownloading ? "Preparing POD…" : "↓ Download complete POD (PDF)"}
          </Box>
          <Typography sx={{ fontSize: 10, color: "#6B7280", mt: 0.5 }}>
            Includes delivery details, signature, photos and delivery history.
          </Typography>
          {downloadErr && (
            <Typography sx={{ fontSize: 10, color: "#DC2626", mt: 0.25 }}>{downloadErr}</Typography>
          )}
        </Box>
      )}

      {/* Proof images — only rendered when persistent data is available */}
      {!noProof && !loading && hasImage && (
        <Box mt={1.25}>
          {photoUri && (
            <Box mb={1}>
              <Typography sx={{ fontSize: 10, color: "#6B7280", mb: 0.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Photo
              </Typography>
              {photoErr ? (
                <Typography sx={{ fontSize: 10, color: "#94A3B8", fontStyle: "italic" }}>
                  Image data present but could not be rendered{isDeliveryStage ? " here — use Download complete POD." : " — download to view."}
                </Typography>
              ) : (
                <Box
                  component="img"
                  src={photoUri}
                  alt="Proof photo"
                  onError={() => setPhotoErr(true)}
                  sx={{
                    width: "100%",
                    maxHeight: 220,
                    objectFit: "cover",
                    borderRadius: "8px",
                    border: "1px solid #E5E7EB",
                    display: "block",
                  }}
                />
              )}
              {/* Delivery stage: the complete POD button above replaces the photo-only link. */}
              {!isDeliveryStage && (
                <>
                  <Box
                    component="button"
                    type="button"
                    disabled={photoDownloading}
                    onClick={handlePhotoDownload}
                    sx={{
                      display: "inline-block",
                      mt: 0.5,
                      p: 0,
                      border: 0,
                      background: "none",
                      fontSize: 10,
                      color: "#4338CA",
                      cursor: photoDownloading ? "wait" : "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {photoDownloading ? "Downloading…" : "↓ Download photo"}
                  </Box>
                  {downloadErr && (
                    <Typography sx={{ fontSize: 10, color: "#DC2626", mt: 0.25 }}>{downloadErr}</Typography>
                  )}
                </>
              )}
            </Box>
          )}
          {sigUri && (
            <Box mb={1}>
              <Typography sx={{ fontSize: 10, color: "#6B7280", mb: 0.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Signature
              </Typography>
              {sigErr ? (
                <Typography sx={{ fontSize: 10, color: "#94A3B8", fontStyle: "italic" }}>
                  Signature data present but could not be rendered{isDeliveryStage ? " here — use Download complete POD." : " — download to view."}
                </Typography>
              ) : (
                <Box
                  component="img"
                  src={sigUri}
                  alt="Recipient signature"
                  onError={() => setSigErr(true)}
                  sx={{
                    width: "100%",
                    maxHeight: 100,
                    objectFit: "contain",
                    borderRadius: "8px",
                    border: "1px solid #E5E7EB",
                    background: "#fff",
                    display: "block",
                    p: 1,
                  }}
                />
              )}
              {!isDeliveryStage && (
                <Box
                  component="a"
                  href={sigUri}
                  download={`proof-sig-${proofRecord?.savedAt ?? "signature"}.png`}
                  sx={{
                    display: "inline-block",
                    mt: 0.5,
                    fontSize: 10,
                    color: "#4338CA",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  ↓ Download signature
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Fallback — captured but no retrievable image data */}
      {!noProof && !loading && !hasImage && captured && (
        <Typography sx={{ fontSize: 10, color: "#94A3B8", mt: 0.5, fontStyle: "italic" }}>
          Proof captured — image not available for this record.
        </Typography>
      )}
    </DrawerSection>
  );
}

function TaskDetailDrawer({ task, onClose }) {
  // ── Proof data fetch ──────────────────────────────────────────────────────
  // Fetches persisted proof records (including images) from GET /tasks/{id}/pod.
  // Falls back to task-blob flags for legacy tasks with no proof records.
  const [proofsData,    setProofsData]    = useState(null);
  const [proofsLoading, setProofsLoading] = useState(false);

  useEffect(() => {
    if (!task?.id) { setProofsData(null); return; }

    const hasAnyProof =
      (task.pickupProofRequirement && task.pickupProofRequirement !== "none") ||
      (task.podRequirement         && task.podRequirement         !== "none");

    if (!hasAnyProof) { setProofsData(null); return; }

    let cancelled = false;
    setProofsLoading(true);
    getTaskProofs(task.id)
      .then(r => r.ok ? r.json() : null)
      .then(d  => { if (!cancelled) setProofsData(d); })
      .catch(() => { if (!cancelled) setProofsData(null); })
      .finally(() => { if (!cancelled) setProofsLoading(false); });

    return () => { cancelled = true; };
  }, [task?.id]);

  const pickupProofRecord   = proofsData?.proofs?.find(p => p.stage === "pickup")   ?? null;
  const deliveryProofRecord = proofsData?.proofs?.find(p => p.stage === "delivery") ?? null;

  // ── History fetch ──────────────────────────────────────────────────────────
  // Fetches the merged execution-event timeline from GET /tasks/{id}/history.
  // Always fires when the drawer opens for any task — history is valuable for
  // both AxisSync-linked and native courier tasks.
  const [historyData,    setHistoryData]    = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!task?.id) { setHistoryData(null); return; }

    let cancelled = false;
    setHistoryLoading(true);
    getTaskHistory(task.id)
      .then(r => r.ok ? r.json() : null)
      .then(d  => { if (!cancelled) setHistoryData(d); })
      .catch(() => { if (!cancelled) setHistoryData(null); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });

    return () => { cancelled = true; };
  }, [task?.id]);
  // ─────────────────────────────────────────────────────────────────────────

  if (!task) return null;

  const isAxisSync   = !!task.linkedCaseId;
  const statusKey    = String(task.status || "assigned").toLowerCase();
  const statusStyle  = STATUS_COLORS_DETAIL[statusKey] || { bg: "#F9FAFB", color: "#374151", border: "#E5E7EB" };
  const surgeonName  = parseSurgeonFromNotes(task.notes);
  const procSummary  = parseProcedureSummary(task.notes);

  const hasTrayCtx      = isAxisSync && (task.trayCount > 0 || task.trayDestination);
  const hasImplantCtx   = isAxisSync && (task.implantTotesSmall > 0 || task.implantTotesMedium > 0 || task.implantTotesLarge > 0 || task.implantDestination);
  const hasTempCtx      = isAxisSync && task.temperatureControl && task.temperatureControl !== "ambient";
  const hasClinical     = isAxisSync && (surgeonName || procSummary || hasTrayCtx || hasImplantCtx || hasTempCtx);

  const fullId    = task.id || task.number || "—";
  const caseRef   = task.caseNumber
    ? `Case ${task.caseNumber}`
    : task.linkedCaseId
    ? `Case ${String(task.linkedCaseId).substring(0, 8).toUpperCase()}`
    : null;

  const risk        = computeRisk(task);
  const deadlineRaw = getDeadline(task);
  const deadlineStr = deadlineRaw
    ? new Date(deadlineRaw).toLocaleString([], {
        month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : null;

  // Build a human-readable tray summary
  const trayLine = hasTrayCtx
    ? `${task.trayCount || 0} ${(task.trayCount || 0) === 1 ? "tray" : "trays"}${task.trayDestination ? ` → ${task.trayDestination}` : ""}`
    : null;

  // Build implant summary
  const implantParts = [];
  if (task.implantTotesSmall  > 0) implantParts.push(`${task.implantTotesSmall} sm`);
  if (task.implantTotesMedium > 0) implantParts.push(`${task.implantTotesMedium} md`);
  if (task.implantTotesLarge  > 0) implantParts.push(`${task.implantTotesLarge} lg`);
  const implantLine = implantParts.length > 0 ? implantParts.join(", ") + " tote" + (implantParts.length > 1 || implantParts[0]?.charAt(0) !== "1" ? "s" : "") : null;

  // Notes: strip [PROC] prefix for clean display, show raw for native tasks
  const rawNotes  = task.notes || "";
  const cleanNotes = rawNotes.startsWith("[PROC]")
    ? rawNotes.replace(/^\[PROC\]\s*[^\n|]+\|?\s*/, "").trim()
    : rawNotes;

  const hasPickupProof   = task.pickupProofRequirement && task.pickupProofRequirement !== "none";
  const hasDeliveryProof = task.podRequirement && task.podRequirement !== "none";
  const showProofSection = hasPickupProof || hasDeliveryProof;

  return (
    <Drawer
      anchor="right"
      open={!!task}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          boxShadow: "none",
          borderLeft: "1px solid #E5E7EB",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: 2.5,
          pt: 2.5,
          pb: 1.5,
          borderBottom: "1px solid #F1F5F9",
          flexShrink: 0,
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.75}>
            <Chip
              label={(task.status || "Assigned")}
              size="small"
              sx={{
                fontSize: 11,
                fontWeight: 800,
                background: statusStyle.bg,
                color: statusStyle.color,
                border: `1px solid ${statusStyle.border}`,
              }}
            />
            <Chip
              label={isAxisSync ? "AxisSync Network" : "Native"}
              size="small"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                background: isAxisSync ? "#EEF2FF" : "#F0FDF4",
                color:      isAxisSync ? "#4338CA"  : "#15803D",
                border:     `1px solid ${isAxisSync ? "#C7D2FE" : "#BBF7D0"}`,
              }}
            />
            {task.isEmergency && (
              <Chip
                label="EMERGENCY"
                size="small"
                sx={{ fontSize: 10, fontWeight: 800, background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA" }}
              />
            )}
          </Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "#374151",
              fontVariantNumeric: "tabular-nums",
              wordBreak: "break-all",
            }}
          >
            {task.publicId || fullId}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mt: 0.25, ml: 1, flexShrink: 0 }}>
          <span style={{ fontSize: 18, lineHeight: 1, color: "#6B7280" }}>✕</span>
        </IconButton>
      </Box>

      {/* ── Scrollable body ── */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2.5 }}>

        {/* Route & Identity */}
        <DrawerSection label="Route & Identity">
          {task.publicId && <DrawerRow label="Order #"     value={task.publicId} mono />}
          <DrawerRow label="Customer"    value={getTaskCustomer(task)} />
          <DrawerRow label="Task Type"   value={task.taskType ? task.taskType.charAt(0).toUpperCase() + task.taskType.slice(1) : "Standard"} />
          {caseRef && <DrawerRow label="Linked Case"  value={caseRef} />}
          {task.linkedCaseId && <DrawerRow label="Case ID"      value={task.linkedCaseId} mono />}
          <DrawerRow label="Created"     value={dt(task.createdAt)} />
          <DrawerRow label="Sequence"    value={task.sequenceOrder != null ? `Stop ${task.sequenceOrder}` : null} />
          <DrawerRow label="Internal ID" value={fullId} mono />
        </DrawerSection>

        <Divider sx={{ mb: 2.5 }} />

        {/* Locations */}
        <DrawerSection label="Locations">
          {task.pickupFacilityName && (
            <DrawerRow label="Pickup Facility" value={task.pickupFacilityName} />
          )}
          <DrawerRow label="Pickup Address"  value={task.pickupAddress} multiline />
          {task.dropoffFacilityName && (
            <DrawerRow label="Dropoff Facility" value={task.dropoffFacilityName || task.hospitalName} />
          )}
          <DrawerRow label="Dropoff Address" value={task.dropoffAddress} multiline />
        </DrawerSection>

        <Divider sx={{ mb: 2.5 }} />

        {/* Assignment & Timing */}
        <DrawerSection label="Assignment & Timing">
          <DrawerRow
            label="Assigned Driver"
            value={
              (task.assignedDriver || task.driver || "").toLowerCase() === "unassigned"
                ? "Unassigned"
                : task.assignedDriver || task.driver || "Unassigned"
            }
          />
          {deadlineStr && (
            <Box display="flex" alignItems="center" gap={1} mb={0.75}>
              <Typography sx={{ fontSize: 11, color: "#6B7280", minWidth: 130, flexShrink: 0 }}>
                Deadline
              </Typography>
              <Box display="flex" alignItems="center" gap={0.75}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#111827" }}>
                  {deadlineStr}
                </Typography>
                {risk.level && (
                  <Chip
                    label={`${risk.level} · ${risk.label}`}
                    size="small"
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      background: "#FFF",
                      color: risk.color,
                      border: `1px solid ${risk.color}`,
                    }}
                  />
                )}
              </Box>
            </Box>
          )}
          <DrawerRow label="Total"        value={money(task.total ?? task.price)} mono />
          <DrawerRow label="Pieces"       value={task.pieceCount ?? task.pieces} />
          <DrawerRow label="Weight"       value={task.weight ? `${task.weight} lb` : null} />
        </DrawerSection>

        {/* Clinical / AxisSync Context — only for linked tasks */}
        {hasClinical && (
          <>
            <Divider sx={{ mb: 2.5 }} />
            <DrawerSection label="AxisSync Case Context">
              {surgeonName  && <DrawerRow label="Surgeon"   value={`Dr. ${surgeonName}`} />}
              {procSummary  && <DrawerRow label="Procedure" value={procSummary} />}
              {trayLine     && <DrawerRow label="Trays"     value={trayLine} />}
              {task.implantDestination && hasImplantCtx && (
                <DrawerRow label="Implant Destination" value={task.implantDestination} />
              )}
              {implantLine  && <DrawerRow label="Implants"  value={implantLine} />}
              {hasTempCtx   && (
                <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                  <Typography sx={{ fontSize: 11, color: "#6B7280", minWidth: 130, flexShrink: 0 }}>
                    Temperature
                  </Typography>
                  <Chip
                    label={`🌡 ${String(task.temperatureControl).charAt(0).toUpperCase() + String(task.temperatureControl).slice(1)}`}
                    size="small"
                    sx={{
                      fontSize: 10, fontWeight: 700,
                      background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE",
                    }}
                  />
                </Box>
              )}
              {task.vendorRepPhone && (
                <DrawerRow label="Vendor Rep Phone" value={task.vendorRepPhone} />
              )}
            </DrawerSection>
          </>
        )}

        {/* Proof Sections */}
        {showProofSection && <Divider sx={{ mb: 2.5 }} />}

        {hasPickupProof && (
          <ProofSection
            label="Proof of Pickup"
            taskId={task.id}
            stage="pickup"
            requirement={task.pickupProofRequirement}
            captured={!!task.pickupProofCaptured || !!pickupProofRecord}
            capturedAt={task.pickupProofReceivedAt || pickupProofRecord?.savedAt}
            recipientName={null}
            proofRecord={pickupProofRecord}
            loading={proofsLoading}
          />
        )}

        {hasDeliveryProof && (
          <ProofSection
            label="Proof of Delivery"
            taskId={task.id}
            publicId={task.publicId}
            stage="delivery"
            requirement={task.podRequirement}
            captured={!!task.podPhotoReceived || !!task.podSignatureReceived || !!deliveryProofRecord}
            capturedAt={task.podReceivedAt || deliveryProofRecord?.savedAt}
            recipientName={task.podRecipientName || deliveryProofRecord?.recipientName || null}
            proofRecord={deliveryProofRecord}
            loading={proofsLoading}
          />
        )}

        {/* Task History */}
        <Divider sx={{ mb: 2.5 }} />
        <TaskHistorySection
          events={historyData?.events}
          hasAuditTrail={historyData?.hasAuditTrail ?? true}
          loading={historyLoading}
        />

        {/* Notes */}
        {cleanNotes && (
          <>
            <Divider sx={{ mb: 2.5 }} />
            <DrawerSection label="Notes & Dispatch Instructions">
              <Box
                sx={{
                  background: "#F8FAFC",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#374151",
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {cleanNotes}
                </Typography>
              </Box>
            </DrawerSection>
          </>
        )}

        {/* Attachments */}
        {task.attachmentUrl && (
          <>
            <Divider sx={{ mb: 2.5 }} />
            <DrawerSection label="Attachments">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  component="a"
                  href={task.attachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#4338CA",
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  📎 PO / Document Attachment
                </Typography>
              </Box>
            </DrawerSection>
          </>
        )}

        {/* Origin metadata (AxisSync administrative context) */}
        {isAxisSync && (task.originLayer || task.originModule || task.createdVia) && (
          <>
            <Divider sx={{ mb: 2.5 }} />
            <DrawerSection label="Origin Metadata">
              <DrawerRow label="Created Via"  value={task.createdVia}   />
              <DrawerRow label="Origin Module" value={task.originModule} />
              <DrawerRow label="Origin Layer"  value={task.originLayer}  />
            </DrawerSection>
          </>
        )}

        {/* Spacer so last section clears the bottom padding */}
        <Box pb={2} />
      </Box>
    </Drawer>
  );
}


// Viewport breakpoint below which the wide, pinned-column table is replaced by
// a card list. On a phone the pinned left (#/Seq/Customer) and right
// (Status/Driver/Actions) column groups together exceed the viewport width and
// would cover every unpinned column (pickup, dropoff, deadline, total...).
const MOBILE_MAX_WIDTH = 900;
function useIsMobile() {
  const query = `(max-width: ${MOBILE_MAX_WIDTH}px)`;
  const get = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches;
  const [isMobile, setIsMobile] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return isMobile;
}

export default function TaskList() {
  const isMobile = useIsMobile();
  const [tasks, setTasks] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "createdAt", dir: "desc" });
  const [overrideOnly, setOverrideOnly] = useState(false);
  // Task detail drawer — null = closed, task object = open
  const [drawerTask, setDrawerTask] = useState(null);
  // Work-source filter: "all" | "native" | "axissync"
  // Default "all" preserves existing behavior — no tasks are hidden unless the
  // dispatcher explicitly selects a source.
  const [sourceFilter, setSourceFilter] = useState("all");

  // Status filter: "active" | "completed" | "all"
  // Default "active" hides delivered/canceled/completed from the main board so
  // finished emergency tasks no longer dominate the dispatcher view.
  const [statusFilter, setStatusFilter] = useState("active");

  // Phase 4: live driver roster for KPI + future dropdown status
  const [liveDrivers, setLiveDrivers] = useState([]);
  useEffect(() => {
    listDrivers()
      .then((r) => r.json())
      .then((d) => setLiveDrivers(Array.isArray(d) ? d : []))
      .catch(() => {});
    const id = setInterval(() => {
      listDrivers()
        .then((r) => r.json())
        .then((d) => setLiveDrivers(Array.isArray(d) ? d : []))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, []);

  // Notification States
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [message, setMessage] = useState("");
  // Real-time dispatcher alert (WebSocket push)
  const [notify, setNotify] = useState({ open: false, message: "" });
  const wsRef         = useRef(null);
  const tableScrollRef = useRef(null);
  const [tableScrollEdge, setTableScrollEdge] = useState({ left: false, right: true });

  const onTableScroll = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    setTableScrollEdge({
      left:  el.scrollLeft > 4,
      right: el.scrollLeft < el.scrollWidth - el.clientWidth - 4,
    });
  }, []);

  const scrollTableBy = useCallback((delta) => {
    tableScrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  }, []);

  const openSuccessSB = (msg) => {
    setMessage(msg);
    setSuccessSB(true);
  };
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = (msg) => {
    setMessage(msg);
    setErrorSB(true);
  };
  const closeErrorSB = () => setErrorSB(false);
  const closeNotify = () => setNotify((prev) => ({ ...prev, open: false }));

  // Stable fetch function — called on mount AND by the WebSocket handler
  const fetchLiveTasks = useCallback(async () => {
    try {
      const res = await getTasks();
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setTasks(data);
          localStorage.setItem(TASKS_KEY, JSON.stringify(data));
          return;
        }
      }
      throw new Error("Backend returned non-array tasks.");
    } catch (err) {
      console.warn("FastAPI unreachable. Falling back to local storage.", err);
      const raw = localStorage.getItem(TASKS_KEY);
      setTasks(raw ? JSON.parse(raw) : []);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchLiveTasks();
  }, [fetchLiveTasks]);

  // WebSocket — real-time dispatcher board updates with auto-reconnect.
  // Render.com's reverse proxy can return 404 on the first WebSocket upgrade
  // attempt if the service is waking from sleep, or drop the connection after
  // ~55 s of inactivity.  We retry with exponential backoff (2 s → 30 s max).
  //
  // 1.0.2 fix: the backend's /ws route requires an authenticated JWT as the
  // `token` query param (see main.py). This client was previously connecting
  // with NO token at all, so every single connection attempt was rejected
  // during the handshake and the socket looped on reconnect forever without
  // ever actually delivering a live update — the dashboard was silently
  // running on manual refresh only. Attach the dashboard's own session JWT
  // (never logged/printed) so the handshake succeeds.
  useEffect(() => {
    const token = getCurrentUser()?.token;
    if (!token) {
      // Not authenticated (or session expired) — nothing to connect with.
      // REST polling (see the effect below) still keeps the board fresh.
      console.warn("[WS] No authenticated session — skipping live socket. REST polling remains active.");
      return;
    }
    const WS_URL = `${API.replace(/^http/, "ws")}/ws?token=${encodeURIComponent(token)}`;
    let destroyed = false;
    let reconnectDelay = 2000;
    let reconnectTimer = null;

    const connect = () => {
      if (destroyed) return;
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("[WS] Dispatcher board connected.");
        reconnectDelay = 2000; // reset backoff after a successful connection
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          // Server sends {"type":"ping"} keepalives every 25 s — ignore them.
          if (data.type === "ping") return;
          if (data.type === "status_update") {
            const who = data.driver || "A driver";
            const what = data.clientName ? `"${data.clientName}"` : "a task";
            const msg = `${who} updated ${what} → ${data.status}`;
            setNotify({ open: true, message: msg });
            // Audio alert — requires /alert.mp3 in the public/ folder
            new Audio("/alert.mp3").play().catch((e) =>
              console.log("Audio play blocked by browser:", e)
            );
            fetchLiveTasks();
          }
          // Backend broadcasts override_requested when a driver submits an
          // override — refresh the task list so the KPI card updates immediately.
          if (data.type === "override_requested") {
            fetchLiveTasks();
          }
          // Backend broadcasts override_decision when a dispatcher approves or
          // denies — refresh so the driver-facing state and badge both update
          // across any open dispatcher sessions.
          if (data.type === "override_decision") {
            fetchLiveTasks();
          }
          // Backend broadcasts task_deleted after a permanent delete — refresh
          // so every other open dashboard tab drops the row immediately too.
          if (data.type === "task_deleted") {
            fetchLiveTasks();
          }
        } catch (e) {
          console.warn("[WS] Message parse error:", e);
        }
      };

      ws.onerror = (e) => console.warn("[WS] Connection error:", e);

      ws.onclose = () => {
        console.log(`[WS] Dispatcher board disconnected. Reconnecting in ${reconnectDelay / 1000}s…`);
        if (!destroyed) {
          reconnectTimer = setTimeout(() => {
            reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
            connect();
          }, reconnectDelay);
        }
      };
    };

    connect();

    return () => {
      destroyed = true;
      clearTimeout(reconnectTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [fetchLiveTasks]);

  // REST polling fallback (1.0.2) — resilience if the WebSocket is down,
  // reconnecting, or unavailable (e.g. blocked by a network policy). The
  // WebSocket above remains the immediate-synchronization path; this interval
  // guarantees the board self-heals within ~20s even if the socket never
  // connects at all. Intentionally independent of WS connection state so it
  // keeps running through disconnects/reconnects.
  useEffect(() => {
    const pollId = setInterval(() => {
      fetchLiveTasks();
    }, 20_000);
    return () => clearInterval(pollId);
  }, [fetchLiveTasks]);

  const save = (next) => {
    localStorage.setItem(TASKS_KEY, JSON.stringify(next));
    setTasks(next);
  };

  // ✅ Mutations by task id (NOT by rows[] index)
  const updateTask = (taskKey, patch) => {
    const next = tasks.map((t) => (getTaskKey(t) === taskKey ? { ...t, ...patch } : t));
    save(next);
  };

  const updateTaskFromServer = (taskKey, updatedTask) => {
    const next = tasks.map((t) => (getTaskKey(t) === taskKey ? { ...t, ...updatedTask } : t));
    save(next);
  };

  const deleteTask = async (taskKey, { force = false } = {}) => {
    try {
      const res = await deleteTaskBackend(taskKey, { force });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 409 && !force) {
          // Backend blocks deletion of tasks with a successful QuickBooks
          // invoice sync unless explicitly forced — surface that as a
          // second, more deliberate confirmation instead of a generic error.
          const confirmForce = window.confirm(
            `${data?.detail || "This task has a successful QuickBooks sync on record."}\n\nDelete anyway?`
          );
          if (confirmForce) return deleteTask(taskKey, { force: true });
          return;
        }
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      // Only remove from local state after the backend confirms deletion —
      // previously this ran unconditionally with no backend call at all,
      // which is why deleted rows reappeared on the next refresh/poll.
      const next = tasks.filter((t) => getTaskKey(t) !== taskKey);
      save(next);
      openSuccessSB("Task deleted");
    } catch (err) {
      openErrorSB(`Delete failed: ${err.message}`);
    }
  };

  const onDelete = (taskKey) => {
    const t = tasks.find((x) => getTaskKey(x) === taskKey);
    if (!t) return;
    if (!window.confirm(`Delete task ${taskKey}?`)) return;
    deleteTask(taskKey);
  };

  const onEdit = (taskKey) => {
    const t = tasks.find((x) => getTaskKey(x) === taskKey);
    if (!t) return;
    localStorage.setItem(EDIT_KEY, JSON.stringify(t));
    // ?edit=1 tells the Create Task screen this navigation is an explicit
    // edit of an existing task, so it's safe to resume the id/createdAt/
    // status that were just stashed above. Any other way of reaching that
    // screen (sidebar link, back button, etc.) must NOT resume this draft.
    window.location.hash = "/applications/task-create?edit=1";
  };

  const updateTaskStatusBackend = async (taskKey, newStatus) => {
    try {
      const res  = await updateTaskStatus(taskKey, newStatus);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      updateTask(taskKey, { status: newStatus });
      openSuccessSB(`Status → "${newStatus}"`);
    } catch (err) {
      openErrorSB(`Status update failed: ${err.message}`);
    }
  };

  const updateTaskDriverBackend = async (taskKey, newDriver) => {
    // Empty string means "clear assignment" — send null to the backend and
    // store null locally so the normaliser never sees the stale "Unassigned" string.
    const value = newDriver || null;
    try {
      const selectedDriver = value
        ? liveDrivers.find((driver) => String(driver.name || "").trim() === value)
        : null;
      const res = value && selectedDriver
        ? await assignTaskDriver(taskKey, selectedDriver.id)
        : await request(`/tasks/${taskKey}`, {
            method: "PUT",
            body: { assignedDriver: value },
          });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      // Keep both field names in sync so the Driver App filter always works
      const confirmedName = data?.driver_name || value;
      updateTask(taskKey, {
        driver: confirmedName,
        assignedDriver: confirmedName,
        assigned_driver_id: selectedDriver?.id || null,
      });
      openSuccessSB(confirmedName ? `Driver assigned: ${confirmedName}` : "Driver cleared");
    } catch (err) {
      openErrorSB(`Driver reassignment failed: ${err.message}`);
    }
  };

  const updateSequence = async (taskKey, newOrder) => {
    const parsed = parseInt(newOrder, 10);
    if (Number.isNaN(parsed) || parsed < 1) return;
    try {
      const res  = await request(`/tasks/${taskKey}/sequence`, {
        method: "PUT",
        body: { sequenceOrder: parsed },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      updateTask(taskKey, { sequenceOrder: parsed });
    } catch (err) {
      openErrorSB(`Sequence update failed: ${err.message}`);
    }
  };

  const handleOverrideDecision = async (taskKey, overrideType, decision) => {
    try {
      const res = decision === "approve"
        ? await approveTaskOverride(taskKey, overrideType)
        : await denyTaskOverride(taskKey, overrideType);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `${res.status}`);
      updateTaskFromServer(taskKey, data?.task || {});
      const typeLabel = overrideType === "arrival" ? "Arrival"
                      : overrideType === "pickup"  ? "Pickup"
                      : "Delivery";
      openSuccessSB(`${typeLabel} override ${decision === "approve" ? "approved" : "denied"}`);
    } catch (err) {
      openErrorSB(`Override ${decision} failed: ${err.message}`);
    }
  };

  /* The QuickBooks Sync Action */
  // Re-evaluate scroll edge indicators when task data or sort changes so the
  // right arrow correctly appears after initial render.
  useEffect(() => { onTableScroll(); }, [tasks, onTableScroll]);

  // Per-task QB sync loading state — prevents double-click during flight.
  const [qbSyncingId, setQbSyncingId] = useState(null);

  /**
   * Extract the first human-readable sentence from a QB fault detail string.
   * QB fault strings look like:
   *   "[6140] Duplicate DocNumber Error: DocNumber=1234567 is already used."
   *   "[6240] Invalid Reference Id: Item 1 has been made inactive."
   * Returns the raw string when it doesn't match the pattern.
   */
  function _parseQbError(detail) {
    if (!detail || typeof detail !== "string") return "QuickBooks sync failed.";
    // Already a clean parsed string from the backend — return as-is
    if (detail.startsWith("[")) return detail;
    // Try to strip a leading "QB Invoice Rejected: " or "QB ... Failed: " prefix
    const match = detail.match(/^[A-Za-z\s]+Failed:\s*(.+)$/i) ||
                  detail.match(/^[A-Za-z\s]+Rejected:\s*(.+)$/i);
    if (match) return match[1].trim();
    return detail;
  }

  const handleInvoiceSync = async (task) => {
    const taskId = task.id || task.number || uuidv4();
    if (qbSyncingId === taskId) return; // already in flight
    setQbSyncingId(taskId);

    try {
      const previewCharges = task.preview || {};
      const charges =
        task.charges || {
          base:            previewCharges.base,
          mileageCharge:   previewCharges.mileage,
          deadheadCharge:  previewCharges.deadhead,
          weightCharge:    previewCharges.weight,
          waitCharge:      previewCharges.wait,
          holdoverCharge:  previewCharges.holdover,
          dedicatedCharge: previewCharges.dedicated,
          afterHoursCharge: previewCharges.afterHours,
          fuelSurcharge:   previewCharges.fuel,
        };

      const lineItems = [];
      const billableMiles  = Math.max(0, (Number(task.liveMiles) || 0) - 20);
      const billableWeight = Math.max(0, (Number(task.weight)   || 0) - 50);

      if (charges.base > 0) {
        const pAddr = task.pickupAddress  ? task.pickupAddress.split(",")[0]  : "Origin";
        const dAddr = task.dropoffAddress ? task.dropoffAddress.split(",")[0] : "Destination";
        lineItems.push({ description: `Base Delivery Rate (${pAddr} to ${dAddr})`, amount: Number(charges.base), quantity: 1 });
      }
      if (charges.mileageCharge > 0)
        lineItems.push({ description: "Live Mileage ($1.90/mi after 20 free)", amount: Number(charges.mileageCharge), quantity: billableMiles });
      if (charges.weightCharge > 0)
        lineItems.push({ description: "Overweight Cargo Surcharge ($0.20/lb after 50 lbs)", amount: Number(charges.weightCharge), quantity: billableWeight });
      if (charges.fuelSurcharge > 0)
        lineItems.push({ description: "Fuel Surcharge", amount: Number(charges.fuelSurcharge), quantity: 1 });
      if (Number(charges.afterHoursCharge || 0) > 0)
        lineItems.push({ description: "After Hours Charge", amount: Number(charges.afterHoursCharge), quantity: 1 });
      if (Number(charges.deadheadCharge || 0) > 0)
        lineItems.push({ description: "Deadhead Mileage", amount: Number(charges.deadheadCharge), quantity: 1 });
      if (Number(charges.waitCharge || 0) > 0)
        lineItems.push({ description: "Wait Time Charge", amount: Number(charges.waitCharge), quantity: 1 });
      if (Number(charges.holdoverCharge || 0) > 0)
        lineItems.push({ description: "Holdover / Overnight Surcharge", amount: Number(charges.holdoverCharge), quantity: 1 });
      if (Number(charges.dedicatedCharge || 0) > 0)
        lineItems.push({ description: "Dedicated Vehicle / Hours", amount: Number(charges.dedicatedCharge), quantity: 1 });

      const res = await syncInvoice(taskId, {
        total:      task.total ?? task.price ?? 0,
        clientName: task.clientName || "Walk-in Customer",
        line_items: lineItems,
      });

      let result = {};
      try { result = await res.json(); } catch { /* non-JSON response */ }

      if (res.ok) {
        const qbId = result.quickbooks_invoice_id || result.qbInvoiceId || "";
        openSuccessSB(`QB Invoice Created${qbId ? ` — #${qbId}` : ""}.`);
      } else {
        const rawDetail = result.detail || result.message || "QuickBooks sync failed.";
        openErrorSB(_parseQbError(rawDetail));
      }
    } catch (err) {
      openErrorSB("Network error — could not reach the server.");
    } finally {
      setQbSyncingId(null);
    }
  };

  /* filtering + sorting */
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();

    // 1. Status filter — applied first so override/source filters work within
    //    the already-scoped set. Default "active" keeps the main board clean.
    let base = tasks;
    if (statusFilter === "active") {
      base = base.filter((t) => !TERMINAL_STATUSES.has((t.status || "").toLowerCase()));
    } else if (statusFilter === "completed") {
      base = base.filter((t) => TERMINAL_STATUSES.has((t.status || "").toLowerCase()));
    }

    // 2. Override filter
    if (overrideOnly) {
      base = base.filter((t) => !!getPendingOverride(t));
    }

    // 3. Work-source filter — "all" shows everything; otherwise restrict by source.
    //    This never alters task data, only which rows the dispatcher sees.
    if (sourceFilter !== "all") {
      base = base.filter((t) => workSource(t) === sourceFilter);
    }

    if (!needle) return base;

    // 4. Text search — includes work-source label so dispatchers can type
    //    "axissync" or "native" to find matching rows.
    return base.filter((t) => {
      const pending = getPendingOverride(t);
      const srcLabel = workSource(t) === "axissync" ? "axissync network" : "native";
      const hay = [
        t.id,
        t.publicId,
        t.number,
        getTaskCustomer(t),
        getTaskDropoffFacility(t),
        t.driver,
        t.pickupAddress,
        t.dropoffAddress,
        t.status,
        srcLabel,
        pending ? `${pending.type} override` : "",
        pending?.reason,
        pending?.requested_by_email,
        pending?.requested_by,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [tasks, q, overrideOnly, sourceFilter, statusFilter]);

  const rows = useMemo(() => {
    const s = SORTERS[sort.key] || SORTERS.createdAt;
    const arr = [...filtered].sort(s);
    return sort.dir === "desc" ? arr.reverse() : arr;
  }, [filtered, sort]);

  // Phase 4: KPI summary values
  const kpis = useMemo(() => {
    const active = tasks.filter((t) => !TERMINAL_STATUSES.has((t.status || "").toLowerCase()));
    const unassigned = active.filter((t) => {
      const d = String(t.assignedDriver || t.driver || "").toLowerCase();
      return !d || d === "unassigned";
    });
    const atRisk = active.filter((t) => {
      const { level } = computeRisk(t);
      return level === "Critical" || level === "High";
    });
    const activeDrivers = liveDrivers.filter(
      (d) => !["available", "offline"].includes((d.operational_status || "").toLowerCase())
    );
    const overrideRequests = active.filter((t) => !!getPendingOverride(t));
    // Work-source split — derived from linkedCaseId presence only, no schema changes.
    const axissyncLinked = active.filter((t) => !!t.linkedCaseId).length;
    return {
      active: active.length,
      unassigned: unassigned.length,
      atRisk: atRisk.length,
      activeDrivers: activeDrivers.length,
      overrideRequests: overrideRequests.length,
      axissyncLinked,
      nativeTasks: active.length - axissyncLinked,
    };
  }, [tasks, liveDrivers]);

  const clickSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const sortArrow = (key) =>
    sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div style={isMobile ? { ...page, margin: "12px 0", padding: "0 8px" } : page}>
      <div style={headerBar}>
        <h2 style={title}>Tasks</h2>
        <div style={actionsRow}>
          <input
            placeholder="Search tasks…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={isMobile ? { ...searchBox, width: "100%" } : searchBox}
          />
          {/* Status filter — Active | Completed | All */}
          {(() => {
            const completedCount = tasks.length - kpis.active;
            const statusTabs = [
              { key: "active",    label: "Active",    count: kpis.active,    border: "#6EE7B7", bg: "#ECFDF5", fg: "#065F46" },
              { key: "completed", label: "Completed", count: completedCount, border: "#D1D5DB", bg: "#F9FAFB", fg: "#374151" },
              { key: "all",       label: "All",       count: tasks.length,   border: "#E5E7EB", bg: "#F3F4F6", fg: "#6B7280" },
            ];
            return statusTabs.map(({ key, label, count, border, bg, fg }) => {
              const sel = statusFilter === key;
              return (
                <button
                  key={key}
                  style={{
                    ...btn,
                    borderColor: sel ? border : "#E5E7EB",
                    background:  sel ? bg    : "#fff",
                    color:       sel ? fg    : "#6b7280",
                    fontWeight:  sel ? 700   : 500,
                  }}
                  onClick={() => setStatusFilter(key)}
                  title={
                    key === "active"
                      ? "Show only active (non-terminal) tasks"
                      : key === "completed"
                      ? "Show delivered, canceled, and completed tasks"
                      : "Show all tasks regardless of status"
                  }
                >
                  {label}
                  <span style={{ marginLeft: 5, color: "#64748b", fontSize: 11 }}>
                    ({count})
                  </span>
                </button>
              );
            });
          })()}

          <span style={{ width: 1, height: 22, background: "#E5E7EB", alignSelf: "center", flexShrink: 0 }} />

          {/* Work-source filter — All | Native | AxisSync Network */}
          {[
            { key: "all",      label: "All Work",       border: "#E5E7EB", bg: "#F9FAFB", fg: "#374151" },
            { key: "native",   label: "Native",         border: "#6EE7B7", bg: "#ECFDF5", fg: "#065F46" },
            { key: "axissync", label: "AxisSync",       border: "#818CF8", bg: "#EEF2FF", fg: "#4338CA" },
          ].map(({ key, label, border, bg, fg }) => {
            const active = sourceFilter === key;
            return (
              <button
                key={key}
                style={{
                  ...btn,
                  borderColor: active ? border : "#E5E7EB",
                  background:  active ? bg    : "#fff",
                  color:       active ? fg    : "#6b7280",
                  fontWeight:  active ? 700   : 500,
                }}
                onClick={() => setSourceFilter(key)}
                title={
                  key === "all"
                    ? "Show all tasks"
                    : key === "native"
                    ? `${kpis.nativeTasks} standalone courier jobs`
                    : `${kpis.axissyncLinked} AxisSync network-linked tasks`
                }
              >
                {label}
                {key !== "all" && (
                  <span style={{ marginLeft: 5, color: "#64748b", fontSize: 11 }}>
                    ({key === "native" ? kpis.nativeTasks : kpis.axissyncLinked})
                  </span>
                )}
              </button>
            );
          })}

          <button
            style={{
              ...btn,
              borderColor: overrideOnly ? "#C4B5FD" : "#E5E7EB",
              background: overrideOnly ? "#F5F3FF" : "#fff",
              color: overrideOnly ? "#6D28D9" : "#374151",
              fontWeight: 700,
            }}
            onClick={() => setOverrideOnly((v) => !v)}
          >
            {overrideOnly
              ? `Override Requests: ${kpis.overrideRequests}`
              : `${kpis.overrideRequests} Override Request${kpis.overrideRequests === 1 ? "" : "s"}`}
          </button>
          <button
            style={btn}
            onClick={() => {
              window.location.hash = "/applications/task-create";
            }}
          >
            + New Task
          </button>
          <button style={btn} onClick={() => exportCsv(rows)}>
            Export CSV
          </button>
          <button
            style={btn}
            onClick={async () => {
              try {
                const result = await syncTasks(rows);
                window.alert(result?.message || "Sync complete");
              } catch (e) {
                window.alert("Sync failed.");
              }
            }}
          >
            Sync All
          </button>
        </div>
      </div>

      {/* ── Phase 4: KPI summary ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        {[
          {
            label: "Active Tasks",
            value: kpis.active,
            color: "#2563EB",
            sub: kpis.active > 0
              ? `${kpis.nativeTasks} native · ${kpis.axissyncLinked} AxisSync`
              : "non-terminal",
          },
          {
            label: "Unassigned",
            value: kpis.unassigned,
            color: kpis.unassigned > 0 ? "#DC2626" : "#16A34A",
            sub: kpis.unassigned > 0 ? "needs attention" : "all covered",
          },
          {
            label: "At Risk / Late",
            value: kpis.atRisk,
            color: kpis.atRisk > 0 ? "#EA580C" : "#16A34A",
            sub: kpis.atRisk > 0 ? "< 2 hours to deadline" : "on track",
          },
          {
            label: "Active Drivers",
            value: kpis.activeDrivers,
            color: "#7C3AED",
            sub: "on shift / in transit",
          },
          {
            label: "Override Requests",
            value: kpis.overrideRequests,
            color: kpis.overrideRequests > 0 ? "#7C3AED" : "#16A34A",
            sub: kpis.overrideRequests > 0 ? "dispatch review required" : "none pending",
          },
        ].map((k) => (
          <div
            key={k.label}
            style={{
              flex: isMobile ? "1 1 calc(50% - 12px)" : 1,
              minWidth: isMobile ? 0 : 140,
              background: "#fff",
              borderRadius: 12,
              padding: "14px 16px",
              boxShadow: "0 2px 8px rgba(2,6,23,0.06)",
              borderTop: `3px solid ${k.color}`,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 4 }}>
              {k.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: k.color, lineHeight: 1 }}>
              {k.value}
            </div>
            <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 4 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div style={card}>
        {/* position: relative scopes the floating scroll arrows */}
        <div style={{ position: "relative" }}>
          {!isMobile && tableScrollEdge.left && (
            <button
              onClick={() => scrollTableBy(-360)}
              style={{ ...scrollArrowBtn, left: 0 }}
              aria-label="Scroll table left"
              title="Scroll left"
            >&#8249;</button>
          )}
          {!isMobile && tableScrollEdge.right && (
            <button
              onClick={() => scrollTableBy(360)}
              style={{ ...scrollArrowBtn, right: 0 }}
              aria-label="Scroll table right"
              title="Scroll right"
            >&#8250;</button>
          )}
        <div ref={tableScrollRef} style={{ overflowX: "auto" }} onScroll={onTableScroll}>
          {isMobile && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, fontSize: 12, color: "#6b7280" }}>
              <label htmlFor="task-sort-mobile" style={{ fontWeight: 700 }}>Sort</label>
              <select
                id="task-sort-mobile"
                value={`${sort.key}:${sort.dir}`}
                onChange={(e) => {
                  const [key, dir] = e.target.value.split(":");
                  setSort({ key, dir });
                }}
                style={{ flex: 1, height: 34, borderRadius: 8, border: "1px solid #e5e7eb", padding: "0 8px", fontSize: 13 }}
              >
                <option value="createdAt:desc">Newest first</option>
                <option value="deadline:asc">Deadline (soonest)</option>
                <option value="urgency:asc">Most urgent</option>
                <option value="status:asc">Status</option>
                <option value="driver:asc">Driver</option>
                <option value="clientName:asc">Customer</option>
                <option value="total:desc">Total (high to low)</option>
              </select>
            </div>
          )}
          <div style={{ minWidth: isMobile ? 0 : tableMinWidth }}>
            <div style={isMobile ? { display: "none" } : headRow}>
              <button
                style={{ ...thBtn, position: "sticky", left: STICKY_ID_L, zIndex: STICKY_HDR_Z, background: STICKY_HDR_BG }}
                onClick={() => clickSort("id")}
              >
                #{sortArrow("id")}
              </button>
              <div
                style={{ ...thBtn, cursor: "default", position: "sticky", left: STICKY_SEQ_L, zIndex: STICKY_HDR_Z, background: STICKY_HDR_BG }}
                title="Route sequence (1 = next stop)"
              >Seq</div>
              <button
                style={{ ...thBtn, position: "sticky", left: STICKY_CLI_L, zIndex: STICKY_HDR_Z, background: STICKY_HDR_BG, boxShadow: "4px 0 6px -3px rgba(0,0,0,0.10)" }}
                onClick={() => clickSort("clientName")}
              >
                Customer{sortArrow("clientName")}
              </button>
              <div>Pickup</div>
              <div>Dropoff</div>
              <button style={thBtn} onClick={() => clickSort("deadline")}>
                Deadline{sortArrow("deadline")}
              </button>
              <button style={thBtn} onClick={() => clickSort("total")}>
                Total{sortArrow("total")}
              </button>
              <div>Pieces</div>
              <div>Weight</div>
              <button style={thBtn} onClick={() => clickSort("createdAt")}>
                Created{sortArrow("createdAt")}
              </button>
              {/* Status + Driver pinned right — always visible without horizontal scrolling */}
              <button
                style={{ ...thBtn, position: "sticky", right: STICKY_STS_R, zIndex: STICKY_HDR_Z, background: STICKY_HDR_BG, boxShadow: "-4px 0 6px -3px rgba(0,0,0,0.10)" }}
                onClick={() => clickSort("status")}
              >
                Status{sortArrow("status")}
              </button>
              <button
                style={{ ...thBtn, whiteSpace: "nowrap", position: "sticky", right: STICKY_DRV_R, zIndex: STICKY_HDR_Z, background: STICKY_HDR_BG }}
                onClick={() => clickSort("driver")}
              >
                Driver{sortArrow("driver")}
              </button>
              <div style={{ position: "sticky", right: STICKY_ACT_R, zIndex: STICKY_HDR_Z, background: STICKY_HDR_BG }}>Actions</div>
            </div>

            {rows.length === 0 ? (
              <div style={{ textAlign: "center", padding: 36, color: "#6b7280" }}>
                {overrideOnly ? "No pending override requests." : "No tasks yet."}
              </div>
            ) : (
              rows.map((t, i) => {
                const taskKey = getTaskKey(t) || String(i);
                const rawStatus = t.status || "Assigned";
                // Display-only normalization so a legacy/in-flight task still
                // sitting on a pre-1.0.2 status (En Route*, Paused) renders a
                // valid, correctly-colored selection instead of an empty
                // dropdown — see normalizeActiveStatusForDisplay() above.
                // The dispatcher only ever picks from the new canonical list;
                // nothing here rewrites the task's stored status.
                const status = normalizeActiveStatusForDisplay(rawStatus, t);
                const emerg = isEmergencyTask(t);
                const statusKey = String(status).toLowerCase();
                // Status chip always reflects the actual status color.
                // Emergency rows are already flagged via row background + red outline —
                // overriding the chip color here only creates inconsistency.
                const statusBg = TASK_STATUS_COLORS[statusKey] || "#fb8c00";
                const pendingOverride = getPendingOverride(t);
                const reassignmentRequired = isReassignmentRequired(t);
                const reassignmentText = reassignmentMessage(t);

                const fullId = t.id || t.number || "—";
                const idShort = t.publicId || (fullId !== "—" ? trunc(String(fullId), 10) : "—");

                // Opaque background for sticky cells — must match (or closely approximate)
                // the row's tinted background so scrolling content doesn't bleed through.
                const stickyBg = getStickyBg(t, i, emerg);

                // Normalise driver value — old records store literal "Unassigned" which has
                // no matching <option> and causes the select to appear blank.
                const rawDriver = String(t.assignedDriver || t.driver || "");
                const driverVal = rawDriver.toLowerCase() === "unassigned" ? "" : rawDriver;

                // Phase 4: risk indicators
                const risk        = computeRisk(t);
                const stalled     = isStalled(t);
                const deadlineRaw = getDeadline(t);
                const deadlineStr = deadlineRaw
                  ? new Date(deadlineRaw).toLocaleString([], {
                      month: "short", day: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })
                  : null;

                // AxisSync operational context — derived only for linked tasks.
                // Native tasks are unaffected: both values will be null.
                const isAxisSyncTask = !!t.linkedCaseId;
                const surgeonName    = isAxisSyncTask ? parseSurgeonFromNotes(t.notes) : null;
                // trayCount and trayDestination are direct task fields set by POST /cases/
                const hasTrayContext = isAxisSyncTask && (t.trayCount > 0);

                if (isMobile) {
                  // Phone / narrow layout: one self-contained card per task so
                  // pickup, dropoff, deadline, status, driver and actions are all
                  // visible without horizontal scrolling or pinned columns.
                  const lineLabel = { fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" };
                  const lineValue = { fontSize: 13, color: "#111827", wordBreak: "break-word" };
                  return (
                    <div
                      key={taskKey}
                      style={{
                        border: "1px solid #E5E7EB",
                        ...riskRowStyle(t, i, emerg),
                        background: stickyBg,
                        borderRadius: 12,
                        padding: 12,
                        marginBottom: 10,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", wordBreak: "break-word" }}>
                            {t.taskType && t.taskType !== "standard" && (
                              <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 4, marginRight: 6, background: t.taskType === "pickup" ? "#DBEAFE" : "#EDE9FE", color: t.taskType === "pickup" ? "#1D4ED8" : "#6D28D9" }}>
                                {t.taskType.toUpperCase()}
                              </span>
                            )}
                            {getTaskCustomer(t) || "—"}
                          </div>
                          <div style={{ fontSize: 11, color: "#6b7280", ...mono }} title={String(fullId)}>
                            #{idShort}
                            {t.linkedCaseId ? ` · AxisSync ${t.caseNumber ? `Case ${t.caseNumber}` : ""}` : ""}
                            {surgeonName ? ` · Dr. ${surgeonName}` : ""}
                            {hasTrayContext ? ` · ${t.trayCount} ${t.trayCount === 1 ? "Tray" : "Trays"}` : ""}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }} title="Route stop order (1 = next stop)">
                          <span style={{ fontSize: 10, fontWeight: 700, color: "#6b7280" }}>Stop</span>
                          <input
                            type="number"
                            min={1}
                            defaultValue={t.sequenceOrder ?? 1}
                            key={`seq-m-${taskKey}-${t.sequenceOrder}`}
                            onBlur={(e) => updateSequence(taskKey, e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") updateSequence(taskKey, e.target.value); }}
                            style={{ width: 44, textAlign: "center", border: "1px solid #E5E7EB", borderRadius: 6, padding: "4px", fontSize: 12, fontWeight: 700 }}
                          />
                        </div>
                      </div>

                      {risk.level ? (
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                          <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#fff", background: risk.color }}>
                            {risk.level} · {risk.label}
                          </span>
                          {deadlineStr && <span style={{ fontSize: 11, color: "#6b7280" }}>{deadlineStr}</span>}
                        </div>
                      ) : (
                        <div>
                          <div style={lineLabel}>Deadline</div>
                          <div style={lineValue}>{dt(t.deliverBy)}</div>
                        </div>
                      )}

                      <div>
                        <div style={lineLabel}>Pickup</div>
                        <div style={lineValue}>{t.pickupAddress || "—"}</div>
                      </div>
                      <div>
                        <div style={lineLabel}>Dropoff</div>
                        <div style={lineValue}>{getDropoffLabel(t, " — ") || "—"}</div>
                      </div>

                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", ...mono }}>
                        <span style={{ fontSize: 12 }}><span style={lineLabel}>Total </span>{money(t.total ?? t.price)}</span>
                        <span style={{ fontSize: 12 }}><span style={lineLabel}>Pieces </span>{t.pieceCount ?? t.pieces ?? "—"}</span>
                        <span style={{ fontSize: 12 }}><span style={lineLabel}>Weight </span>{t.weight ? `${t.weight} lb` : "—"}</span>
                        <span style={{ fontSize: 12 }}><span style={lineLabel}>Created </span>{dt(t.createdAt)}</span>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={lineLabel}>Status</div>
                          <select
                            value={status}
                            onChange={(e) => updateTaskStatusBackend(taskKey, e.target.value)}
                            style={{ width: "100%", padding: "8px", borderRadius: 6, border: "none", background: statusBg, color: "#ffffff", fontWeight: 700, fontSize: 12 }}
                          >
                            {TASK_STATUSES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={lineLabel}>Driver</div>
                          <select
                            value={driverVal}
                            onChange={(e) => updateTaskDriverBackend(taskKey, e.target.value)}
                            style={{ width: "100%", padding: "8px", borderRadius: 6, border: driverVal ? "1.5px solid #E5E7EB" : "1.5px solid #FCA5A5", background: "#fff", color: driverVal ? "#374151" : "#9CA3AF", fontWeight: driverVal ? 700 : 400, fontSize: 12 }}
                          >
                            <option value="">— Unassigned —</option>
                            {liveDrivers.map((d) => (
                              <option key={d.id} value={d.name}>
                                {d.name} · {d.current_status || d.operational_status || "Available"}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {(pendingOverride || reassignmentRequired || (!driverVal && !reassignmentRequired) || stalled) && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 11, fontWeight: 800 }}>
                          {pendingOverride && <span style={{ color: "#6D28D9" }}>{pendingOverride.type.toUpperCase()} OVERRIDE REQUESTED</span>}
                          {reassignmentRequired && <span style={{ color: "#DC2626" }} title={reassignmentText}>Driver Removed — Reassign Immediately</span>}
                          {!driverVal && !reassignmentRequired && <span style={{ color: "#DC2626" }}>Needs Assignment</span>}
                          {stalled && <span style={{ color: "#CA8A04" }}>Status Stalled</span>}
                        </div>
                      )}

                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        <button style={{ ...btn, color: "#1D4ED8", borderColor: "#BFDBFE", background: "#EFF6FF", fontWeight: 700 }} onClick={() => setDrawerTask(t)}>
                          Inspect
                        </button>
                        {pendingOverride && (
                          <>
                            <button style={{ ...btn, color: "#6D28D9", borderColor: "#C4B5FD", background: "#F5F3FF", fontWeight: 700 }} onClick={() => handleOverrideDecision(taskKey, pendingOverride.type, "approve")}>
                              Approve Override
                            </button>
                            <button style={{ ...btn, color: "#B45309", borderColor: "#FCD34D", background: "#FFFBEB", fontWeight: 700 }} onClick={() => handleOverrideDecision(taskKey, pendingOverride.type, "deny")}>
                              Deny Override
                            </button>
                          </>
                        )}
                        <button
                          style={{ ...btnQB, ...(qbSyncingId === (t.id || t.number) ? { opacity: 0.55, cursor: "wait" } : {}) }}
                          disabled={!!qbSyncingId}
                          onClick={() => handleInvoiceSync(t)}
                        >
                          {qbSyncingId === (t.id || t.number) ? "Syncing…" : "QB Invoice"}
                        </button>
                        <button style={btn} onClick={() => onEdit(taskKey)}>Edit</button>
                        <button style={btnDanger} onClick={() => onDelete(taskKey)}>Delete</button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={taskKey}
                    style={{ ...row, ...riskRowStyle(t, i, emerg) }}
                  >
                    <div
                      style={{ ...cell, ...mono, ...cellEllipsis, position: "sticky", left: STICKY_ID_L, zIndex: STICKY_ROW_Z, background: stickyBg }}
                      title={String(fullId)}
                    >
                      {idShort}
                    </div>

                    {/* Route Sequence */}
                    <div style={{ position: "sticky", left: STICKY_SEQ_L, zIndex: STICKY_ROW_Z, background: stickyBg }}>
                      <input
                        type="number"
                        min={1}
                        defaultValue={t.sequenceOrder ?? 1}
                        key={`seq-${taskKey}-${t.sequenceOrder}`}
                        onBlur={(e) => updateSequence(taskKey, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") updateSequence(taskKey, e.target.value); }}
                        style={{
                          width: 48,
                          textAlign: "center",
                          border: "1px solid #E5E7EB",
                          borderRadius: 6,
                          padding: "4px 4px",
                          fontSize: 12,
                          fontWeight: 700,
                          color: (t.sequenceOrder ?? 1) === 1 ? "#10B981" : "#374151",
                          background: (t.sequenceOrder ?? 1) === 1 ? "#ECFDF5" : "#fff",
                        }}
                        title="Route stop order (1 = next stop)"
                      />
                    </div>

                    {/* Client / Hospital — Phase 4 */}
                    <div style={{ ...cell, overflow: "hidden", minWidth: 0, position: "sticky", left: STICKY_CLI_L, zIndex: STICKY_ROW_Z, background: stickyBg, boxShadow: "4px 0 6px -3px rgba(0,0,0,0.10)" }}>
                      {/* Primary line: type badge (shrink-proof) + client name (ellipsis) */}
                      <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                        {t.taskType && t.taskType !== "standard" && (
                          <span
                            style={{
                              flexShrink: 0,
                              fontSize: 9,
                              fontWeight: 800,
                              padding: "1px 5px",
                              borderRadius: 4,
                              lineHeight: "14px",
                              background: t.taskType === "pickup" ? "#DBEAFE" : "#EDE9FE",
                              color:      t.taskType === "pickup" ? "#1D4ED8" : "#6D28D9",
                              letterSpacing: "0.05em",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t.taskType.toUpperCase()}
                          </span>
                        )}
                        <span
                          style={{
                            display: "block",
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                          title={getTaskCustomer(t)}
                        >
                          {getTaskCustomer(t) || "—"}
                        </span>
                      </div>
                      {/* Line 2: work-source indicator
                          AxisSync-linked  → indigo chip + case ref
                          Native (no link) → nothing (clean default, no label noise) */}
                      {t.linkedCaseId ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}>
                          <span
                            style={{
                              flexShrink: 0,
                              fontSize: 8,
                              fontWeight: 800,
                              letterSpacing: "0.06em",
                              padding: "1px 5px",
                              borderRadius: 3,
                              lineHeight: "14px",
                              background: "#EEF2FF",
                              color: "#4338CA",
                              border: "1px solid #C7D2FE",
                              whiteSpace: "nowrap",
                            }}
                          >
                            AXISSYNC
                          </span>
                          <span
                            style={{
                              fontSize: 9,
                              color: "#6B7280",
                              fontVariantNumeric: "tabular-nums",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {t.caseNumber
                              ? `Case ${t.caseNumber}`
                              : `Case ${String(t.linkedCaseId).substring(0, 8).toUpperCase()}`}
                          </span>
                        </div>
                      ) : null}

                      {/* Line 3: AxisSync operational context — surgeon + tray load.
                          Only rendered when the task has linked case data and at least
                          one piece of context is present.  Native tasks see nothing. */}
                      {isAxisSyncTask && (surgeonName || hasTrayContext) && (
                        <div
                          style={{
                            marginTop: 2,
                            fontSize: 9,
                            color: "#6B7280",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={[
                            surgeonName  ? `Surgeon: ${surgeonName}`                              : null,
                            hasTrayContext ? `Trays: ${t.trayCount} ${t.trayDestination || ""}` : null,
                          ].filter(Boolean).join(" · ")}
                        >
                          {surgeonName && (
                            <span style={{ fontWeight: 600 }}>Dr. {surgeonName}</span>
                          )}
                          {surgeonName && hasTrayContext && (
                            <span style={{ margin: "0 3px", opacity: 0.4 }}>·</span>
                          )}
                          {hasTrayContext && (
                            <span>{t.trayCount} {t.trayCount === 1 ? "Tray" : "Trays"} {t.trayDestination || ""}</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div style={{ ...cell, ...cellEllipsis, minWidth: 0 }} title={t.pickupAddress || ""}>
                      {t.pickupAddress || "—"}
                    </div>

                    <div style={{ ...cell, ...cellEllipsis, minWidth: 0 }} title={getDropoffLabel(t, "\n")}>
                      {getDropoffLabel(t) || "—"}
                    </div>

                    {/* Deadline / Risk countdown — Phase 4 */}
                    <div style={{ overflow: "hidden", minWidth: 0 }}>
                      {risk.level ? (
                        <>
                          {/* Primary line: risk pill */}
                          <div style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 7px",
                                borderRadius: 999,
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#fff",
                                background: risk.color,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {risk.level} · {risk.label}
                            </span>
                          </div>
                          {/* Secondary line: exact datetime */}
                          {deadlineStr && (
                            <div
                              style={{
                                fontSize: 10,
                                color: "#6b7280",
                                marginTop: 3,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {deadlineStr}
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ ...cell, ...mono, ...cellEllipsis }}>{dt(t.deliverBy)}</div>
                      )}
                    </div>

                    <div style={{ ...cell, ...mono }} title={tooltip(t)}>
                      {money(t.total ?? t.price)}
                    </div>

                    <div style={{ ...cell, ...mono }}>{t.pieceCount ?? t.pieces ?? "—"}</div>

                    <div style={{ ...cell, ...mono }}>{t.weight ? `${t.weight} lb` : "—"}</div>

                    <div style={{ ...cell, ...mono }}>{dt(t.createdAt)}</div>

                    {/* Status — sticky right, always visible */}
                    <div style={{ minWidth: 0, position: "sticky", right: STICKY_STS_R, zIndex: STICKY_ROW_Z, background: stickyBg, boxShadow: "-4px 0 6px -3px rgba(0,0,0,0.10)" }}>
                      <select
                        value={status}
                        onChange={(e) => updateTaskStatusBackend(taskKey, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "5px 8px",
                          borderRadius: 6,
                          border: "none",
                          background: statusBg,
                          color: "#ffffff",
                          fontWeight: 700,
                          fontSize: 11,
                          cursor: "pointer",
                          outline: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                          appearance: "none",
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23ffffff'/%3E%3C/svg%3E")`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 8px center",
                          backgroundSize: "10px 6px",
                          paddingRight: 28,
                        }}
                      >
                        {TASK_STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {pendingOverride && (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 9,
                            fontWeight: 800,
                            color: "#6D28D9",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={`${pendingOverride.type.toUpperCase()} OVERRIDE REQUESTED${pendingOverride.reason ? ` — ${pendingOverride.reason}` : ""}`}
                        >
                          {pendingOverride.type.toUpperCase()} OVERRIDE REQUESTED
                        </div>
                      )}
                      {reassignmentRequired && (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 9,
                            fontWeight: 800,
                            color: "#DC2626",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={reassignmentText}
                        >
                          DRIVER REMOVED
                        </div>
                      )}
                    </div>

                    {/* Driver — sticky right, always visible */}
                    <div style={{ minWidth: 0, position: "sticky", right: STICKY_DRV_R, zIndex: STICKY_ROW_Z, background: stickyBg }}>
                      <select
                        value={driverVal}
                        onChange={(e) => updateTaskDriverBackend(taskKey, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "4px 6px",
                          borderRadius: 6,
                          border: driverVal ? "1.5px solid #E5E7EB" : "1.5px solid #FCA5A5",
                          background: "#fff",
                          color: driverVal ? "#374151" : "#9CA3AF",
                          fontWeight: driverVal ? 700 : 400,
                          fontSize: 11,
                          cursor: "pointer",
                          outline: "none",
                        }}
                      >
                        <option value="">— Unassigned —</option>
                        {liveDrivers.map((d) => (
                          <option key={d.id} value={d.name}>
                            {d.name} · {d.current_status || d.operational_status || "Available"}
                          </option>
                        ))}
                      </select>
                      {/* Single secondary-line signal — highest priority only */}
                      {(() => {
                        if (reassignmentRequired)
                          return (
                            <div
                              style={{ fontSize: 9, fontWeight: 800, color: "#DC2626", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                              title={reassignmentText}
                            >
                              Driver Removed — Reassign Immediately
                            </div>
                          );
                        if (!driverVal)
                          return <div style={{ fontSize: 9, fontWeight: 700, color: "#DC2626", marginTop: 3, whiteSpace: "nowrap" }}>Needs Assignment</div>;
                        if (risk.level === "Critical" || risk.level === "High")
                          return <div style={{ fontSize: 9, fontWeight: 700, marginTop: 3, whiteSpace: "nowrap", color: risk.level === "Critical" ? "#DC2626" : "#EA580C" }}>At Risk</div>;
                        if (stalled)
                          return <div style={{ fontSize: 9, fontWeight: 700, color: "#CA8A04", marginTop: 3, whiteSpace: "nowrap" }}>Status Stalled</div>;
                        return null;
                      })()}
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "nowrap", position: "sticky", right: STICKY_ACT_R, zIndex: STICKY_ROW_Z, background: stickyBg }}>
                      {/* Inspect — opens read-only detail drawer, never mutates task */}
                      <button
                        style={{
                          ...btn,
                          color: "#1D4ED8",
                          borderColor: "#BFDBFE",
                          background: "#EFF6FF",
                          fontWeight: 700,
                        }}
                        onClick={() => setDrawerTask(t)}
                        title="Open task detail panel"
                      >
                        Inspect
                      </button>
                      {pendingOverride && (
                        <>
                          <button
                            style={{
                              ...btn,
                              color: "#6D28D9",
                              borderColor: "#C4B5FD",
                              background: "#F5F3FF",
                              fontWeight: 700,
                            }}
                            onClick={() => handleOverrideDecision(taskKey, pendingOverride.type, "approve")}
                            title={`Approve ${pendingOverride.type} override`}
                          >
                            Approve Override
                          </button>
                          <button
                            style={{
                              ...btn,
                              color: "#B45309",
                              borderColor: "#FCD34D",
                              background: "#FFFBEB",
                              fontWeight: 700,
                            }}
                            onClick={() => handleOverrideDecision(taskKey, pendingOverride.type, "deny")}
                            title={`Deny ${pendingOverride.type} override`}
                          >
                            Deny Override
                          </button>
                        </>
                      )}
                      <button
                        style={{
                          ...btnQB,
                          ...(qbSyncingId === (t.id || t.number)
                            ? { opacity: 0.55, cursor: "wait" }
                            : {}),
                        }}
                        disabled={!!qbSyncingId}
                        onClick={() => handleInvoiceSync(t)}
                      >
                        {qbSyncingId === (t.id || t.number) ? "Syncing…" : "QB Invoice"}
                      </button>
                      <button style={btn} onClick={() => onEdit(taskKey)}>
                        Edit
                      </button>
                      <button style={btnDanger} onClick={() => onDelete(taskKey)}>
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>{/* end overflowX scroll */}
        </div>{/* end position: relative arrow scope */}
      </div>

      {/* Notification Snackbars */}
      <MDSnackbar
        color="success"
        icon="check"
        title="QuickBooks Synced"
        content={message}
        dateTime={new Date().toLocaleString()}
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="Sync Error"
        content={message}
        dateTime={new Date().toLocaleString()}
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
      </div>

      {/* ── Task detail inspection drawer ── */}
      <TaskDetailDrawer
        task={drawerTask}
        onClose={() => setDrawerTask(null)}
      />

      {/* ── Real-time driver status alert ── */}
      <Snackbar
        open={notify.open}
        autoHideDuration={5000}
        onClose={closeNotify}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 7 }}
      >
        <Alert
          onClose={closeNotify}
          severity="info"
          variant="filled"
          sx={{
            width: "100%",
            fontSize: 13,
            fontWeight: 600,
            background: "#1e293b",
            color: "#fff",
            "& .MuiAlert-icon": { color: "#38bdf8" },
          }}
        >
          {notify.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}





























